import { createPublicClient, http } from "viem";
import { sei } from "viem/chains";
import tokenService from "../../common/tokenService.js";

// Default configuration for viem implementation
export const defaultConfig = {
  apiUrl: "https://prodapi.symphony.ag",
  timeout: 10000,
  chainId: 1329,
  chainName: "sei",
  rpcUrl: "https://evm-rpc.sei-apis.com",
  nativeAddress: "0x0",
  wrappedNativeAddress: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7",
  slippage: "1",
  publicClient: undefined,
  functionName: "executeSwaps",
  multicall: "0xcA11bde05977b3631167028862bE2a173976CA11",
  additionalTokens: {},
  overrideDefaultTokens: false, // When true, only use additionalTokens instead of merging with default list
  feeParams: {
    paramFee: "0",
    feeAddress: "0x0000000000000000000000000000000000000000",
    feeSharePercentage: "0",
  },
};

// Create the initial publicClient
const initialPublicClient = createPublicClient({
  chain: sei,
  transport: http(defaultConfig.rpcUrl),
});

// Initialize the current configuration with the publicClient
let currentConfig = { ...defaultConfig, publicClient: initialPublicClient };

export const getConfig = () => {
  // Use tokenService to get merged token list
  const tokens = tokenService.getStaticTokenList(
    currentConfig.chainId,
    currentConfig.additionalTokens || {},
    currentConfig.overrideDefaultTokens || false,
    currentConfig.nativeAddress
  );

  return {
    ...currentConfig,
    tokens,
  };
};

export const setConfig = (newConfig = {}) => {
  // Validate functionName if provided
  if (newConfig.functionName && newConfig.functionName !== "executeSwaps") {
    throw new Error('Invalid functionName. Must be either "executeSwaps"');
  }

  // Validate and normalize additionalTokens if provided using tokenService
  if (newConfig.additionalTokens) {
    // Let tokenService handle validation - it will log warnings for invalid tokens
    // and return only valid ones, so we don't need to throw errors here
    newConfig.additionalTokens = tokenService._validateAdditionalTokens(
      newConfig.additionalTokens
    );
  }

  //Validate feeParams
  if (newConfig?.feeParams) {
    // First, check if all required fields are present
    if (
      newConfig.feeParams.feeAddress === undefined ||
      newConfig.feeParams.paramFee === undefined ||
      newConfig.feeParams.feeSharePercentage === undefined
    )
      throw new Error(
        "Missing split fee parameters! paramFee, feeAddress, feeShareePercentage parameters are required for enabling custom fee split behavior."
      );

    //check fee address is valid
    if (!/^0x[a-fA-F0-9]{40}$/.test(newConfig.feeParams.feeAddress))
      throw new Error("feeAddress must be a valid ethereum address!");

    //check fee share perc. is valid (use isNaN to allow zero values)
    if (isNaN(Number(newConfig.feeParams.feeSharePercentage)))
      throw new Error(
        "feeSharePercentage must be a number or string representing a number! "
      );
    //check fee share perc. is between 0 - 100%
    if (
      Number(newConfig.feeParams.feeSharePercentage) > 10000 ||
      Number(newConfig.feeParams.feeSharePercentage) < 0
    )
      throw new Error(
        "feeSharePercentage must represent a percentage in bps. Should be between 0 and 10000. (10000 = 100%)"
      );

    //check fee param perc. is valid (use isNaN to allow zero values)
    if (isNaN(Number(newConfig.feeParams.paramFee)))
      throw new Error(
        "paramFee must be a number or string representing a number! "
      );
    //check paramFee perc. is between 0 - 100%
    if (
      Number(newConfig.feeParams.paramFee) > 10000 ||
      Number(newConfig.feeParams.paramFee) < 0
    )
      throw new Error(
        "paramFee must represent a percentage in bps. Should be between 0 and 10000. (10000 = 100%)"
      );
  }

  // Note: Native address changes are now handled by tokenService.getStaticTokenList()
  // when getConfig() is called, so we don't need to modify TokenListByAddress here

  // Check if rpcUrl has changed and recreate publicClient if needed
  const shouldRecreateClient =
    newConfig.rpcUrl && newConfig.rpcUrl !== currentConfig.rpcUrl;

  // Update the current config
  currentConfig = {
    ...currentConfig,
    ...newConfig,
  };
  // Recreate publicClient if rpcUrl has changed
  if (shouldRecreateClient) {
    const updatedPublicClient = createPublicClient({
      chain: sei,
      transport: http(currentConfig.rpcUrl),
    });
    currentConfig.publicClient = updatedPublicClient;
  }
};

export const resetConfig = () => {
  // Create a new publicClient with the default rpcUrl
  const defaultPublicClient = createPublicClient({
    chain: sei,
    transport: http(defaultConfig.rpcUrl),
  });

  // Reset to default configuration
  currentConfig = { ...defaultConfig, publicClient: defaultPublicClient };
};
